import React, { useState } from 'react';
import SEO from '../../components/SEO';
import { generateArticle } from '../../utils/structuredData';
import seoData from '../../config/seoData';
import { MessageCircle, Instagram, Menu, X, ArrowLeft, Calendar, Clock, Building2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { useNavigate, Link } from 'react-router-dom';

const LimpezaCondominios = () => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleWhatsAppClick = () => {
    const message = encodeURIComponent('Olá! Vi o artigo sobre limpeza em condomínios e gostaria de um orçamento.');
    window.open(`https://wa.me/5531971823249?text=${message}`, '_blank');
  };

  return (
    <div className="min-h-screen
      <SEO 
        title={seoData['/blog/limpeza-condominios'].title}
        description={seoData['/blog/limpeza-condominios'].description}
        canonical={seoData['/blog/limpeza-condominios'].canonical}
      /> bg-white">
      <header className="bg-slate-900 text-white py-4 px-6 sticky top-0 z-40 shadow-lg">
        <div className="container mx-auto flex justify-between items-center">
          <div className="flex items-center space-x-3 cursor-pointer" onClick={() => navigate('/')}>
            <img 
              src="https://customer-assets.emergentagent.com/job_locacao-pisos/artifacts/9nveujmi_file_00000000e2f071f5a40c0fc04e1c9bed.png" 
              alt="WellTec Logo" 
              className="h-16 w-auto"
            />
          </div>
          
          <div className="hidden md:flex items-center space-x-6">
            <Link to="/" className="hover:text-blue-400 transition-colors">Início</Link>
            <Link to="/servicos" className="hover:text-blue-400 transition-colors">Serviços</Link>
            <Link to="/limpeza-pos-obra" className="hover:text-blue-400 transition-colors">Limpeza Pós-Obra</Link>
            <Link to="/blog" className="text-green-400 font-semibold">Blog</Link>
            <a href="https://www.instagram.com/welltecbrasil/" target="_blank" rel="noopener noreferrer" className="hover:text-blue-400 transition-colors">
              <Instagram className="w-5 h-5" />
            </a>
            <Button onClick={handleWhatsAppClick} className="bg-green-500 hover:bg-green-600">
              <MessageCircle className="w-4 h-4 mr-2" />
              WhatsApp
            </Button>
          </div>

          <button className="md:hidden text-white" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden absolute top-full left-0 right-0 bg-slate-900 border-t border-slate-700 shadow-xl">
            <div className="flex flex-col space-y-1 p-4">
              <button onClick={() => { navigate('/'); setMobileMenuOpen(false); }} className="text-left py-3 px-4 hover:bg-slate-800 rounded">Início</button>
              <button onClick={() => { navigate('/servicos'); setMobileMenuOpen(false); }} className="text-left py-3 px-4 hover:bg-slate-800 rounded">Serviços</button>
              <button onClick={() => { navigate('/limpeza-pos-obra'); setMobileMenuOpen(false); }} className="text-left py-3 px-4 hover:bg-slate-800 rounded">Limpeza Pós-Obra</button>
              <button onClick={() => { navigate('/blog'); setMobileMenuOpen(false); }} className="text-left py-3 px-4 hover:bg-slate-800 rounded text-green-400 font-semibold">Blog</button>
              <Button onClick={() => { handleWhatsAppClick(); setMobileMenuOpen(false); }} className="bg-green-500 hover:bg-green-600 w-full mt-2">
                <MessageCircle className="w-4 h-4 mr-2" />WhatsApp
              </Button>
            </div>
          </div>
        )}
      </header>

      <article className="py-12 bg-white">
        <div className="container mx-auto px-6 max-w-4xl">
          <button onClick={() => navigate('/blog')} className="flex items-center text-blue-600 hover:text-blue-800 mb-6 transition-colors">
            <ArrowLeft className="w-4 h-4 mr-2" />Voltar para o Blog
          </button>

          <header className="mb-8">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Limpeza Pós-Obra em Condomínios: Áreas Comuns e Garagens
            </h1>
            
            <div className="flex flex-wrap items-center gap-4 text-gray-600 text-sm">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4" /><span>Janeiro 2025</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4" /><span>8 min de leitura</span>
              </div>
            </div>
          </header>

          <div className="prose prose-lg max-w-none">
            <p className="text-lg md:text-xl text-gray-700 leading-relaxed mb-6">
              Condomínios que passam por obras ou reformas em áreas comuns precisam de <strong>limpeza pós-obra especializada</strong> para garantir segurança, higiene e boa apresentação aos moradores. Veja como realizar esse serviço.
            </p>

            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mt-10 mb-4">
              Áreas Mais Afetadas em Condomínios
            </h2>

            <Card className="bg-blue-50 border-2 border-blue-200 mb-6">
              <CardContent className="p-6">
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <Building2 className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700"><strong>Garagens:</strong> Resíduos de cimento, pó, manchas de óleo e tinta</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Building2 className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700"><strong>Halls e corredores:</strong> Sujeira em pisos, paredes e elevadores</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Building2 className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700"><strong>Salão de festas:</strong> Pó acumulado em pisos e janelas</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Building2 className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700"><strong>Áreas externas:</strong> Piscina, playground, churrasqueira</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Building2 className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700"><strong>Fachada e entrada:</strong> Primeira impressão do condomínio</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mt-10 mb-4">
              Limpeza de Garagem Pós-Obra
            </h2>

            <p className="text-gray-700 mb-4">
              A garagem é uma das áreas mais críticas:
            </p>

            <h3 className="text-xl md:text-2xl font-bold text-gray-900 mt-8 mb-4">
              Processo de Limpeza em Garagem:
            </h3>
            <ol className="list-decimal list-inside text-gray-700 space-y-2 mb-6">
              <li><strong>Remoção de entulho grosso</strong> e restos de materiais</li>
              <li><strong>Varredura industrial</strong> para retirar pó e terra</li>
              <li><strong>Remoção de manchas</strong> de cimento e tinta com produtos específicos</li>
              <li><strong>Lavagem com lavadora de piso</strong> industrial</li>
              <li><strong>Limpeza de ralos</strong> e caixas de gordura</li>
              <li><strong>Aspiração final</strong> de cantos e frestas</li>
            </ol>

            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mt-10 mb-4">
              Checklist: Áreas Comuns
            </h2>

            <div className="bg-white border-2 border-gray-200 rounded-lg p-6 mb-6">
              <h4 className="font-bold text-gray-900 mb-4">Hall de Entrada:</h4>
              <ul className="list-disc list-inside text-gray-700 space-y-1 mb-4">
                <li>Limpeza de piso e rodapés</li>
                <li>Limpeza de vidros e espelhos</li>
                <li>Polimento de metais (corrimãos, maçanetas)</li>
                <li>Limpeza de elevadores</li>
              </ul>

              <h4 className="font-bold text-gray-900 mb-4">Salão de Festas:</h4>
              <ul className="list-disc list-inside text-gray-700 space-y-1 mb-4">
                <li>Limpeza profunda de pisos</li>
                <li>Limpeza de cozinha e churrasqueira</li>
                <li>Limpeza de banheiros</li>
                <li>Janelas e cortinas</li>
              </ul>

              <h4 className="font-bold text-gray-900 mb-4">Piscina e Área Externa:</h4>
              <ul className="list-disc list-inside text-gray-700 space-y-1">
                <li>Limpeza de deck e bordas</li>
                <li>Limpeza de playground</li>
                <li>Varredura de calçadas</li>
                <li>Limpeza de áreas verdes</li>
              </ul>
            </div>

            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mt-10 mb-4">
              Equipamentos Necessários
            </h2>

            <p className="text-gray-700 mb-4">
              Para áreas grandes de condomínios:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 mb-6">
              <li><strong>Lavadora de piso industrial:</strong> Para garagens e áreas extensas</li>
              <li><strong>Varredeira industrial:</strong> Para limpeza inicial rápida</li>
              <li><strong>Aspiradores profissionais:</strong> Seco e líquido</li>
              <li><strong>Equipamentos de altura:</strong> Para limpeza de fachadas</li>
              <li><strong>Hidrojateadora:</strong> Para áreas externas muito sujas</li>
            </ul>

            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mt-10 mb-4">
              Tempo Estimado
            </h2>

            <div className="bg-white border-2 border-gray-200 rounded-lg overflow-hidden mb-6">
              <table className="w-full">
                <thead className="bg-slate-900 text-white">
                  <tr>
                    <th className="p-4 text-left">Área do Condomínio</th>
                    <th className="p-4 text-left">Tempo Estimado</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b">
                    <td className="p-4">Garagem pequena (até 200m²)</td>
                    <td className="p-4 font-semibold">1 dia</td>
                  </tr>
                  <tr className="border-b">
                    <td className="p-4">Garagem média (200-500m²)</td>
                    <td className="p-4 font-semibold">1-2 dias</td>
                  </tr>
                  <tr className="border-b">
                    <td className="p-4">Garagem grande (500m²+)</td>
                    <td className="p-4 font-semibold">2-3 dias</td>
                  </tr>
                  <tr>
                    <td className="p-4">Áreas comuns completas</td>
                    <td className="p-4 font-semibold">3-5 dias</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mt-10 mb-4">
              Custo Médio (BH - 2025)
            </h2>

            <ul className="list-disc list-inside text-gray-700 space-y-2 mb-6">
              <li><strong>Garagem pequena:</strong> R$ 1.500 - R$ 2.500</li>
              <li><strong>Garagem média:</strong> R$ 2.500 - R$ 4.500</li>
              <li><strong>Garagem grande:</strong> R$ 4.500 - R$ 8.000</li>
              <li><strong>Áreas comuns completas:</strong> R$ 5.000 - R$ 15.000</li>
            </ul>

            <Card className="bg-green-50 border-2 border-green-200 mb-6">
              <CardContent className="p-6">
                <h3 className="font-bold text-gray-900 mb-3">💡 Dica para Síndicos</h3>
                <p className="text-gray-700">
                  Negocie um pacote completo (garagem + áreas comuns) para obter melhores preços. Empresas especializadas oferecem descontos para serviços combinados.
                </p>
              </CardContent>
            </Card>

            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mt-10 mb-4">
              Comunicação com Moradores
            </h2>

            <p className="text-gray-700 mb-4">
              Durante a limpeza pós-obra:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 mb-6">
              <li>Avise os moradores com antecedência sobre horários</li>
              <li>Sinalize áreas interditadas temporariamente</li>
              <li>Informe sobre restrições de acesso (garagem, elevadores)</li>
              <li>Compartilhe cronograma de conclusão</li>
            </ul>

            <Card className="bg-blue-900 text-white mb-6">
              <CardContent className="p-8 text-center">
                <h3 className="text-2xl font-bold mb-4">Limpeza Pós-Obra para Condomínios</h3>
                <p className="mb-6">
                  A WellTec atende condomínios em BH com equipamentos industriais e equipe especializada
                </p>
                <Button 
                  onClick={handleWhatsAppClick}
                  size="lg"
                  className="bg-green-600 hover:bg-green-700 text-white px-8 py-6"
                >
                  <MessageCircle className="w-5 h-5 mr-2" />
                  Solicitar Orçamento para Condomínio
                </Button>
              </CardContent>
            </Card>
          </div>

          <div className="mt-12 pt-8 border-t border-gray-200">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Artigos Relacionados</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <Card className="hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate('/blog/custo-limpeza-pos-obra')}>
                <CardContent className="p-6">
                  <h4 className="font-bold text-gray-900 mb-2">Quanto Custa Limpeza Pós-Obra?</h4>
                  <p className="text-gray-600 text-sm">Guia completo de preços</p>
                </CardContent>
              </Card>
              <Card className="hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate('/blog/limpeza-galpao-industrial')}>
                <CardContent className="p-6">
                  <h4 className="font-bold text-gray-900 mb-2">Limpeza de Galpão Industrial</h4>
                  <p className="text-gray-600 text-sm">Técnicas para grandes áreas</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </article>

      <footer className="bg-slate-900 text-white py-12 mt-12">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <div>
              <h3 className="font-bold mb-4">WellTec</h3>
              <p className="text-gray-400 text-sm">Limpeza pós-obra profissional em BH e região</p>
            </div>
            <div>
              <h3 className="font-bold mb-4">Serviços</h3>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li><Link to="/limpeza-pos-obra" className="hover:text-green-400">Limpeza Pós-Obra</Link></li>
                <li><Link to="/locacao-lavadora-piso" className="hover:text-green-400">Locação de Equipamentos</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="font-bold mb-4">Contato</h3>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li><a href="tel:+5531971823249" className="hover:text-green-400">(31) 97182-3249</a></li>
                <li><button onClick={handleWhatsAppClick} className="hover:text-green-400">WhatsApp</button></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-slate-800 mt-8 pt-8 text-center text-gray-400 text-sm">
            <p>&copy; {new Date().getFullYear()} WellTec. Todos os direitos reservados.</p>
          </div>
        </div>
      </footer>

      <button 
        onClick={handleWhatsAppClick}
        className="fixed bottom-6 right-6 bg-green-500 hover:bg-green-600 text-white rounded-full p-4 shadow-2xl hover:scale-110 transition-all z-50"
        aria-label="WhatsApp"
      >
        <MessageCircle className="w-6 h-6" />
      </button>
    </div>
  );
};

export default LimpezaCondominios;
